function formatTime(seconds) {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0:00';
  const s = Math.floor(seconds);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

function initMoodMusicLibrary(sectionEl) {
  if (!(sectionEl instanceof HTMLElement)) return;
  if (sectionEl.dataset.mmlInitialized === 'true') return;
  sectionEl.dataset.mmlInitialized = 'true';

  const debug =
    document.documentElement.classList.contains('shopify-design-mode') ||
    new URLSearchParams(window.location.search).has('mml_debug');

  const groups = Array.from(sectionEl.querySelectorAll('[data-mml-group]'));
  const player = sectionEl.querySelector('[data-mml-player]');
  const audio = sectionEl.querySelector('[data-mml-audio]');
  const nowTitle = sectionEl.querySelector('[data-mml-now-playing-title]');
  const nowSubtitle = sectionEl.querySelector('[data-mml-now-playing-subtitle]');
  const btnPrev = sectionEl.querySelector('[data-mml-prev]');
  const btnNext = sectionEl.querySelector('[data-mml-next]');
  const btnToggle = sectionEl.querySelector('[data-mml-toggle]');
  const btnClose = sectionEl.querySelector('[data-mml-close]');
  const iconPlay = sectionEl.querySelector('[data-mml-icon-play]');
  const iconPause = sectionEl.querySelector('[data-mml-icon-pause]');
  const timeCurrent = sectionEl.querySelector('[data-mml-time-current]');
  const timeDuration = sectionEl.querySelector('[data-mml-time-duration]');
  const seek = sectionEl.querySelector('[data-mml-seek]');

  if (!(player instanceof HTMLElement) || !(audio instanceof HTMLAudioElement)) return;

  /**
   * Enable mouse "click + drag" scrolling on tab strips.
   * @param {HTMLElement} scroller
   */
  const enableDragScroll = (scroller) => {
    if (scroller.dataset.dragScroll === 'true') return;
    scroller.dataset.dragScroll = 'true';

    /** @type {number | null} */
    let activePointerId = null;
    let isDown = false;
    let startX = 0;
    let startScrollLeft = 0;
    let moved = false;
    let suppressClick = false;

    const setDraggingClass = (dragging) => {
      scroller.classList.toggle('is-dragging', dragging);
    };

    scroller.addEventListener(
      'pointerdown',
      (e) => {
        if (!(e instanceof PointerEvent)) return;
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        // Don't start drag when clicking a form control inside.
        const target = e.target instanceof Element ? e.target : null;
        if (target?.closest('input, textarea, select')) return;

        activePointerId = e.pointerId;
        isDown = true;
        moved = false;
        suppressClick = false;
        startX = e.clientX;
        startScrollLeft = scroller.scrollLeft;
      },
      { passive: false }
    );

    scroller.addEventListener(
      'pointermove',
      (e) => {
        if (!(e instanceof PointerEvent)) return;
        if (!isDown || activePointerId !== e.pointerId) return;
        const dx = e.clientX - startX;
        if (!moved && Math.abs(dx) > 4) {
          moved = true;
          setDraggingClass(true);
          // Only capture the pointer once we confirm a drag — capturing on
          // pointerdown redirects pointerup to the scroller which prevents
          // click events from reaching child buttons.
          try {
            scroller.setPointerCapture(e.pointerId);
          } catch {
            // ignore
          }
        }
        if (!moved) return;

        scroller.scrollLeft = startScrollLeft - dx;
        suppressClick = true;
        e.preventDefault();
      },
      { passive: false }
    );

    const end = (e) => {
      if (!(e instanceof PointerEvent)) return;
      if (activePointerId !== e.pointerId) return;
      isDown = false;
      activePointerId = null;
      if (moved) setDraggingClass(false);
      if (suppressClick) {
        // Block the click that fires after a drag.
        setTimeout(() => {
          suppressClick = false;
        }, 0);
      }
    };

    scroller.addEventListener('pointerup', end);
    scroller.addEventListener('pointercancel', end);

    scroller.addEventListener(
      'click',
      (e) => {
        if (!suppressClick) return;
        e.preventDefault();
        e.stopPropagation();
      },
      true
    );
  };

  let currentPlaylist = [];
  let currentIndex = -1;
  let isSeeking = false;
  /** @type {HTMLElement | null} */
  let currentPanel = sectionEl.querySelector('.mood-music-library__panel.is-active');

  if (debug) {
    console.groupCollapsed('[MoodMusicLibrary] init', {
      sectionId: sectionEl.id,
      groups: groups.map((g) => ({
        group: g.getAttribute('data-mml-group'),
        blockCount: g.getAttribute('data-mml-debug-count'),
      })),
    });
    console.log('panels', sectionEl.querySelectorAll('.mood-music-library__panel').length);
    console.log('tracks', sectionEl.querySelectorAll('[data-mml-track]').length);
    console.groupEnd();
  }

  const getPlaylistFromPanel = (panel) => {
    if (!panel) return [];
    if (debug) {
      console.log('[MoodMusicLibrary] active panel', {
        label: panel.getAttribute('data-mml-debug-label'),
        categoryHandle: panel.getAttribute('data-mml-debug-category-handle'),
        categoryId: panel.getAttribute('data-mml-debug-category-id'),
        tracksCount: panel.querySelector('[data-mml-debug-tracks-count]')?.getAttribute('data-mml-debug-tracks-count'),
      });
    }
    return Array.from(panel.querySelectorAll('[data-mml-track]')).map((btn) => ({
      el: btn,
      url: btn.getAttribute('data-mml-audio-url') || '',
      title: btn.getAttribute('data-mml-track-title') || 'Track',
      category: btn.getAttribute('data-mml-category-title') || '',
    }));
  };

  const getActivePanelFallback = () =>
    currentPanel || sectionEl.querySelector('.mood-music-library__panel.is-active');

  const setPlayingUI = (playing) => {
    if (iconPlay instanceof HTMLElement) iconPlay.hidden = playing;
    if (iconPause instanceof HTMLElement) iconPause.hidden = !playing;
    if (btnToggle instanceof HTMLElement) btnToggle.setAttribute('aria-label', playing ? 'Pause' : 'Play');
  };

  const highlightCurrent = () => {
    sectionEl.querySelectorAll('[data-mml-track].is-playing').forEach((el) => el.classList.remove('is-playing'));
    const item = currentPlaylist[currentIndex];
    if (item?.el) item.el.classList.add('is-playing');
  };

  const loadTrack = async (index, { autoplay = true, panel = null } = {}) => {
    const targetPanel = panel || getActivePanelFallback();
    if (targetPanel) currentPanel = targetPanel;

    currentPlaylist = getPlaylistFromPanel(currentPanel);
    if (!currentPlaylist.length) return;
    const safeIndex = Math.max(0, Math.min(index, currentPlaylist.length - 1));
    currentIndex = safeIndex;

    const track = currentPlaylist[currentIndex];
    if (!track?.url) return;

    if (nowTitle) nowTitle.textContent = track.title;
    if (nowSubtitle) nowSubtitle.textContent = track.category || '';

    player.hidden = false;
    audio.src = track.url;
    audio.currentTime = 0;
    highlightCurrent();

    if (autoplay) {
      try {
        await audio.play();
        setPlayingUI(true);
      } catch {
        setPlayingUI(false);
      }
    } else {
      setPlayingUI(false);
    }
  };

  const playNext = async () => {
    currentPlaylist = getPlaylistFromPanel(getActivePanelFallback());
    if (!currentPlaylist.length) return;
    const next = currentIndex + 1 >= currentPlaylist.length ? 0 : currentIndex + 1;
    await loadTrack(next, { autoplay: true, panel: currentPanel });
  };

  const playPrev = async () => {
    currentPlaylist = getPlaylistFromPanel(getActivePanelFallback());
    if (!currentPlaylist.length) return;
    const prev = currentIndex - 1 < 0 ? currentPlaylist.length - 1 : currentIndex - 1;
    await loadTrack(prev, { autoplay: true, panel: currentPanel });
  };

  // Tabs
  groups.forEach((groupEl) => {
    const tabs = Array.from(groupEl.querySelectorAll('[role="tab"]'));
    const panels = Array.from(groupEl.querySelectorAll('[role="tabpanel"]'));
    const tabScroller = groupEl.querySelector('.mood-music-library__tabs');
    if (tabScroller instanceof HTMLElement) enableDragScroll(tabScroller);

    const setActive = (tab) => {
      if (debug) {
        console.log('[MoodMusicLibrary] setActive tab', {
          group: groupEl.getAttribute('data-mml-group'),
          tabText: tab.textContent?.trim(),
          controls: tab.getAttribute('aria-controls'),
        });
      }
      tabs.forEach((t) => {
        const isActive = t === tab;
        t.classList.toggle('is-active', isActive);
        t.setAttribute('aria-selected', isActive ? 'true' : 'false');
        t.tabIndex = isActive ? 0 : -1;
      });

      const targetId = tab.getAttribute('aria-controls');
      const targetPanel = panels.find((p) => p.id === targetId) || null;
      panels.forEach((p) => {
        const isActive = p.id === targetId;
        p.classList.toggle('is-active', isActive);
        p.hidden = !isActive;
      });

      // If nothing is selected yet, set this group's active panel as current context.
      if (currentIndex === -1 && targetPanel) {
        currentPanel = targetPanel;
        currentPlaylist = getPlaylistFromPanel(currentPanel);
      }
    };

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => setActive(tab));
      tab.addEventListener('keydown', (e) => {
        if (!(e instanceof KeyboardEvent)) return;
        const idx = tabs.indexOf(tab);
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          const next = tabs[(idx + 1) % tabs.length];
          next?.focus();
          setActive(next);
        }
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          const prev = tabs[(idx - 1 + tabs.length) % tabs.length];
          prev?.focus();
          setActive(prev);
        }
      });
    });
  });

  // Track click
  sectionEl.addEventListener('click', (e) => {
    const target = e.target instanceof Element ? e.target.closest('[data-mml-track]') : null;
    if (!(target instanceof HTMLElement)) return;

    const panel = target.closest('.mood-music-library__panel');
    if (panel instanceof HTMLElement) currentPanel = panel;

    currentPlaylist = getPlaylistFromPanel(currentPanel);
    const idx = currentPlaylist.findIndex((t) => t.el === target);
    if (idx === -1) return;

    if (debug) {
      console.log('[MoodMusicLibrary] click track', {
        index: idx,
        title: currentPlaylist[idx]?.title,
        url: currentPlaylist[idx]?.url,
        panelLabel: currentPanel?.getAttribute('data-mml-debug-label'),
      });
    }
    void loadTrack(idx, { autoplay: true, panel: currentPanel });
  });

  // Player controls
  btnPrev?.addEventListener('click', () => void playPrev());
  btnNext?.addEventListener('click', () => void playNext());
  btnToggle?.addEventListener('click', async () => {
    if (!audio.src) {
      currentPlaylist = getPlaylistFromPanel(getActivePanelFallback());
      if (!currentPlaylist.length) return;
      await loadTrack(0, { autoplay: true, panel: currentPanel });
      return;
    }
    if (audio.paused) {
      try {
        await audio.play();
        setPlayingUI(true);
      } catch {
        setPlayingUI(false);
      }
    } else {
      audio.pause();
      setPlayingUI(false);
    }
  });

  btnClose?.addEventListener('click', () => {
    audio.pause();
    audio.removeAttribute('src');
    player.hidden = true;
    currentIndex = -1;
    currentPlaylist = [];
    setPlayingUI(false);
    sectionEl.querySelectorAll('[data-mml-track].is-playing').forEach((el) => el.classList.remove('is-playing'));
  });

  // Audio events
  audio.addEventListener('play', () => setPlayingUI(true));
  audio.addEventListener('pause', () => setPlayingUI(false));
  audio.addEventListener('ended', () => void playNext());

  audio.addEventListener('loadedmetadata', () => {
    if (timeDuration) timeDuration.textContent = formatTime(audio.duration);
  });

  audio.addEventListener('timeupdate', () => {
    if (isSeeking) return;
    if (timeCurrent) timeCurrent.textContent = formatTime(audio.currentTime);
    if (seek instanceof HTMLInputElement) {
      const pct = audio.duration ? (audio.currentTime / audio.duration) * 100 : 0;
      seek.value = String(pct);
    }
  });

  if (seek instanceof HTMLInputElement) {
    const startSeeking = () => {
      isSeeking = true;
    };
    const stopSeeking = () => {
      isSeeking = false;
    };

    // On mobile, the audio `timeupdate` can fight the thumb drag unless we
    // enter "seeking" state as soon as the user touches/presses the range.
    seek.addEventListener('pointerdown', startSeeking);
    seek.addEventListener('pointerup', stopSeeking);
    seek.addEventListener('pointercancel', stopSeeking);
    seek.addEventListener('touchstart', startSeeking, { passive: true });
    seek.addEventListener('touchend', stopSeeking, { passive: true });
    seek.addEventListener('touchcancel', stopSeeking, { passive: true });
    seek.addEventListener('mousedown', startSeeking);
    seek.addEventListener('mouseup', stopSeeking);

    seek.addEventListener('input', () => {
      isSeeking = true;
      const pct = Number.parseFloat(seek.value || '0');
      const nextTime = audio.duration ? (pct / 100) * audio.duration : 0;
      if (timeCurrent) timeCurrent.textContent = formatTime(nextTime);
    });

    seek.addEventListener('change', () => {
      const pct = Number.parseFloat(seek.value || '0');
      audio.currentTime = audio.duration ? (pct / 100) * audio.duration : 0;
      isSeeking = false;
    });
  }

  // Initial playlist for next/prev.
  currentPlaylist = getPlaylistFromPanel(getActivePanelFallback());
}

function boot(root = document) {
  const sections = root.querySelectorAll('.mood-music-library');
  sections.forEach((sectionEl) => initMoodMusicLibrary(sectionEl));
}

document.addEventListener('DOMContentLoaded', () => boot(document));
document.addEventListener('shopify:section:load', (e) => {
  if (e.target instanceof HTMLElement) boot(e.target);
});


